'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  HttpEditor,
  Spinner,
  StreamingMessagesPanel,
  mapHarWebSocketMessagesToStreamingView,
  type HttpEditorAction,
  type ChatMentionsMenuItemType,
} from '@terra/ui-design-system';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from '../../utils/formatHttpForPanel';
import { normalizeHttpVersionNumber } from '@terra/shared-utils';
import { getDefaultStatusMessage } from '../../utils/httpFormatter';
import {
  SourceCodeIcon,
  File01Icon,
  GitCompareIcon,
  Message01Icon,
  ReplayIcon,
} from '@terra/ui-icons';
import { useMemo, useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { EmptyState } from '../EmptyState';
import {
  SelectHttpContextForChatDialog,
  type SelectHttpContextForChatDialogState,
} from '../dialogs/SelectHttpContextForChatDialog';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../../utils/httpMentionUtils';
import type { HARRequest } from '../../hooks/api/mapHarEntryToHarRequest';
import { useHttpHistoryGrpcBodyView } from '../../hooks/useHttpHistoryGrpcBodyView';
import {
  resolveHarEntryRequestBodyText,
  resolveHarEntryRequestDisplayContent,
  resolveHarEntryResponseBodyText,
  resolveHarEntryResponseDisplayContent,
} from '@terra/shared-utils';

const stringifyMaybeJsonBody = (
  body: Record<string, unknown> | string | undefined,
): string | undefined => {
  if (body === undefined) {
    return undefined;
  }

  return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
};

type RequestDetailsPanelProps = {
  applicationId?: string;
  selectedRequest: HARRequest | null;
  isLoadingFullHar: boolean;
  isFullHarError: boolean;
  onReplay: (request: HARRequest) => void;
  openDecoderWith: (text: string) => void;
  addRequestToComparer: (
    request: {
      id: string;
      method: string;
      url: string;
      headers: Record<string, string | string[]>;
      rawHeaders?: string[];
      body: string;
      httpVersion?: string;
      timestamp: string;
      status?: number;
      responseHeaders?: Record<string, string | string[]>;
      responseRawHeaders?: string[];
      responseBody?: string;
      responseHttpVersion?: string;
    },
    response: undefined,
    type: 'request' | 'response',
  ) => void;
  appendToInput: (text: string) => void;
  sendHttpMentionsToChat: ({
    mentions,
    contextMessage,
  }: {
    mentions: ChatMentionsMenuItemType[];
    contextMessage?: string;
  }) => void;
};

export function RequestDetailsPanel({
  applicationId,
  selectedRequest,
  isLoadingFullHar,
  isFullHarError,
  onReplay,
  openDecoderWith,
  addRequestToComparer,
  appendToInput,
  sendHttpMentionsToChat,
}: RequestDetailsPanelProps) {
  const tFullHar = useTranslations('httpHistory.fullHar');
  const [httpContextDialogState, setHttpContextDialogState] =
    useState<SelectHttpContextForChatDialogState | null>(null);
  const { bodyView, grpcHarDisplay, editorProps } = useHttpHistoryGrpcBodyView({
    applicationId,
    harRequest: selectedRequest,
  });
  const responseToolbarSpacer = (
    <Box w="5" h="5" flexShrink={0} aria-hidden visibility="hidden" />
  );
  const handleHttpContextDialogClose = useCallback(() => {
    setHttpContextDialogState(null);
  }, []);
  const resolveRequestBodyText = useCallback(
    (request: HARRequest): string | undefined =>
      resolveHarEntryRequestBodyText({
        harEntry: grpcHarDisplay,
        bodyView,
        fallbackBody: stringifyMaybeJsonBody(request.requestBody),
      }),
    [bodyView, grpcHarDisplay],
  );
  const resolveResponseBodyText = useCallback(
    (request: HARRequest): string =>
      resolveHarEntryResponseBodyText({
        harEntry: grpcHarDisplay,
        bodyView,
        fallbackBody:
          typeof request.responseBody === 'string'
            ? request.responseBody
            : JSON.stringify(request.responseBody, null, 2),
      }),
    [bodyView, grpcHarDisplay],
  );
  const createResponseMention = useCallback(() => {
    if (!selectedRequest) {
      return null;
    }

    const responseBodyStr = resolveResponseBodyText(selectedRequest);

    return createHttpResponseMention({
      id: selectedRequest.id,
      method: selectedRequest.method,
      url: selectedRequest.url,
      statusCode: selectedRequest.status,
      statusText: getDefaultStatusMessage(selectedRequest.status),
      headers: selectedRequest.headers.response,
      rawHeaders: selectedRequest.rawHeaders.response,
      body: responseBodyStr,
      httpVersion: normalizeHttpVersionNumber(
        selectedRequest.responseHttpVersion,
      ),
    });
  }, [selectedRequest, resolveResponseBodyText]);
  const requestContent = useMemo(() => {
    if (!selectedRequest) {
      return '';
    }

    const fallbackContent = formatHttpRequestRaw({
      method: selectedRequest.method,
      url: selectedRequest.url,
      headers: selectedRequest.headers.request,
      rawHeaders: selectedRequest.rawHeaders.request,
      body: stringifyMaybeJsonBody(selectedRequest.requestBody),
      httpVersion: normalizeHttpVersionNumber(selectedRequest.httpVersion),
    });

    return resolveHarEntryRequestDisplayContent({
      harEntry: grpcHarDisplay,
      bodyView,
      fallbackContent,
    });
  }, [selectedRequest, bodyView, grpcHarDisplay]);
  const responseContent = useMemo(() => {
    if (!selectedRequest) {
      return '';
    }

    const fallbackBody =
      typeof selectedRequest.responseBody === 'string'
        ? selectedRequest.responseBody
        : JSON.stringify(selectedRequest.responseBody, null, 2);
    const fallbackContent = formatHttpResponseRaw({
      status: selectedRequest.status,
      statusMessage: getDefaultStatusMessage(selectedRequest.status),
      headers: selectedRequest.headers.response,
      rawHeaders: selectedRequest.rawHeaders.response,
      body: fallbackBody,
      httpVersion: normalizeHttpVersionNumber(
        selectedRequest.responseHttpVersion,
      ),
    });

    return resolveHarEntryResponseDisplayContent({
      harEntry: grpcHarDisplay,
      bodyView,
      fallbackContent,
    });
  }, [selectedRequest, bodyView, grpcHarDisplay]);
  const websocketMessages = useMemo(
    () =>
      mapHarWebSocketMessagesToStreamingView(
        selectedRequest?.webSocketMessages,
      ),
    [selectedRequest],
  );
  const requestActions: HttpEditorAction[] = useMemo(() => {
    if (!selectedRequest) {
      return [];
    }

    return [
      {
        id: 'send-to-decoder',
        label: 'Send to Decoder',
        icon: SourceCodeIcon,
        useSelection: true,
        onClick: (content, selectedText) => {
          const text = selectedText || content;

          if (text) {
            openDecoderWith(text);
          }
        },
      },
      {
        id: 'send-to-replay',
        label: 'Send to Replay',
        icon: ReplayIcon,
        onClick: () => {
          onReplay(selectedRequest);
        },
      },
      {
        id: 'send-to-comparer',
        label: 'Send to Comparer',
        icon: GitCompareIcon,
        onClick: () => {
          addRequestToComparer(
            {
              id: selectedRequest.id,
              method: selectedRequest.method,
              url: selectedRequest.url,
              headers: selectedRequest.headers.request,
              rawHeaders: selectedRequest.rawHeaders.request,
              body: resolveRequestBodyText(selectedRequest) || '',
              httpVersion: normalizeHttpVersionNumber(
                selectedRequest.httpVersion,
              ),
              timestamp: selectedRequest.timestamp,
            },
            undefined,
            'request',
          );
        },
      },
      {
        id: 'send-to-chat',
        label: 'Send to Chat',
        icon: Message01Icon,
        useSelection: true,
        onClick: (content, selectedText) => {
          if (selectedText) {
            appendToInput(selectedText);

            return;
          }

          const mention = createHttpRequestMention({
            id: selectedRequest.id,
            method: selectedRequest.method,
            url: selectedRequest.url,
            headers: selectedRequest.headers.request,
            rawHeaders: selectedRequest.rawHeaders.request,
            body: resolveRequestBodyText(selectedRequest),
            httpVersion: normalizeHttpVersionNumber(
              selectedRequest.httpVersion,
            ),
          });

          if (selectedRequest.status > 0) {
            const responseMention = createResponseMention();

            if (!responseMention) {
              return;
            }

            setHttpContextDialogState({
              requestMention: mention,
              responseMention,
            });

            return;
          }

          sendHttpMentionsToChat({
            mentions: [mention],
          });
        },
      },
    ];
  }, [
    selectedRequest,
    openDecoderWith,
    onReplay,
    addRequestToComparer,
    appendToInput,
    sendHttpMentionsToChat,
    createResponseMention,
    resolveRequestBodyText,
  ]);
  const responseActions: HttpEditorAction[] = useMemo(() => {
    if (!selectedRequest) {
      return [];
    }

    return [
      {
        id: 'send-to-decoder',
        label: 'Send to Decoder',
        icon: SourceCodeIcon,
        useSelection: true,
        onClick: (content, selectedText) => {
          const text = selectedText || content;

          if (text) {
            openDecoderWith(text);
          }
        },
      },
      {
        id: 'send-to-replay',
        label: 'Send to Replay',
        icon: ReplayIcon,
        onClick: () => {
          onReplay(selectedRequest);
        },
      },
      {
        id: 'send-to-comparer',
        label: 'Send to Comparer',
        icon: GitCompareIcon,
        onClick: () => {
          addRequestToComparer(
            {
              id: selectedRequest.id,
              method: selectedRequest.method,
              url: selectedRequest.url,
              headers: selectedRequest.headers.request,
              rawHeaders: selectedRequest.rawHeaders.request,
              body: resolveRequestBodyText(selectedRequest) || '',
              httpVersion: normalizeHttpVersionNumber(
                selectedRequest.httpVersion,
              ),
              timestamp: selectedRequest.timestamp,
              status: selectedRequest.status,
              responseHeaders: selectedRequest.headers.response,
              responseRawHeaders: selectedRequest.rawHeaders.response,
              responseBody: resolveResponseBodyText(selectedRequest),
              responseHttpVersion: selectedRequest.responseHttpVersion,
            },
            undefined,
            'response',
          );
        },
      },
      {
        id: 'send-to-chat',
        label: 'Send to Chat',
        icon: Message01Icon,
        useSelection: true,
        onClick: (content, selectedText) => {
          if (selectedText) {
            appendToInput(selectedText);

            return;
          }

          const mention = createResponseMention();

          if (!mention) {
            return;
          }

          sendHttpMentionsToChat({
            mentions: [mention],
          });
        },
      },
    ];
  }, [
    selectedRequest,
    openDecoderWith,
    onReplay,
    addRequestToComparer,
    appendToInput,
    sendHttpMentionsToChat,
    createResponseMention,
    resolveRequestBodyText,
    resolveResponseBodyText,
  ]);

  if (isLoadingFullHar) {
    return (
      <VStack h="full" w="full" alignItems="center" justifyContent="center">
        <Spinner size="md" />
      </VStack>
    );
  }

  if (isFullHarError || !selectedRequest) {
    return (
      <EmptyState
        icon={File01Icon}
        title={tFullHar('detailsLoadErrorTitle')}
        description={tFullHar('detailsLoadErrorDescription')}
      />
    );
  }

  return (
    <>
      <VStack alignItems="stretch" w="100%" h="100%" gap="0">
        <HStack
          alignItems="stretch"
          w="full"
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.muted"
          flexShrink={0}
        >
          <Box
            flex="1"
            px="2"
            py="1"
            minH="8"
            display="flex"
            alignItems="center"
          >
            <MetaDefault fontWeight="medium">Request</MetaDefault>
          </Box>
          <Box w="1px" bg="border.subtle" alignSelf="stretch" />
          <Box
            flex="1"
            px="2"
            py="1"
            minH="8"
            display="flex"
            alignItems="center"
          >
            <HStack gap="2">
              <MetaDefault fontWeight="medium">Response</MetaDefault>
              <MetaDefault color="fg.muted">
                {selectedRequest.status}
              </MetaDefault>
            </HStack>
          </Box>
        </HStack>
        <HStack alignItems="stretch" w="100%" flex="1" minH={0} gap="0">
          <Box flex="1" display="flex" flexDirection="column" minH={0} minW={0}>
            <Box flex="1" minH={0}>
              <HttpEditor
                value={requestContent}
                readOnly
                showViewModeToggle
                {...editorProps}
                customActions={requestActions}
                onSendToReplay={() => onReplay(selectedRequest)}
              />
            </Box>
          </Box>
          <Box w="1px" bg="border.subtle" flexShrink={0} />
          <Box flex="1" display="flex" flexDirection="column" minH={0} minW={0}>
            <Box flex="1" minH={0}>
              <HttpEditor
                value={responseContent}
                readOnly
                showViewModeToggle
                {...editorProps}
                toolbarContent={responseToolbarSpacer}
                customActions={responseActions}
              />
            </Box>
          </Box>
        </HStack>
        {websocketMessages.length > 0 ? (
          <Box
            flex="1"
            minH={0}
            w="full"
            borderTopWidth="1px"
            borderColor="border.muted"
          >
            <StreamingMessagesPanel
              messages={websocketMessages}
              height="100%"
            />
          </Box>
        ) : null}
      </VStack>
      <SelectHttpContextForChatDialog
        state={httpContextDialogState}
        onClose={handleHttpContextDialogClose}
      />
    </>
  );
}
